import Button from "@/components/Button";
import Dialog from "@/components/Dialog";

export type CareerPlanStatusDialogMode = "publish" | "unpublish" | "complete";

const STATUS_DIALOG_COPY = {
  publish: {
    headline: "Publish Career Plan?",
    body: "Publishing this plan makes it visible to the member on their career profile. You can unpublish it at any time.",
    confirm: "Publish",
    confirming: "Publishing...",
    confirmTestId: "confirm-publish-career-plan",
    cancelTestId: "cancel-publish-career-plan"
  },
  unpublish: {
    headline: "Unpublish Career Plan?",
    body: "Unpublishing hides this plan from the member's career profile. You can publish it again at any time.",
    confirm: "Unpublish",
    confirming: "Unpublishing...",
    confirmTestId: "confirm-unpublish-career-plan",
    cancelTestId: "cancel-unpublish-career-plan"
  },
  complete: {
    headline: "Complete Career Plan?",
    body: "Completing this plan marks it as finished. Completed plans cannot be edited, published, or unpublished.",
    confirm: "Complete",
    confirming: "Completing...",
    confirmTestId: "confirm-complete-career-plan",
    cancelTestId: "cancel-complete-career-plan"
  }
} as const;

export type CareerPlanStatusDialogProps = {
  open: boolean;
  mode: CareerPlanStatusDialogMode | null;
  saving?: boolean;
  error?: string | null;
  onClose(): void;
  onConfirm(): void;
};

export default function CareerPlanStatusDialog({
  open,
  mode,
  saving = false,
  error,
  onClose,
  onConfirm
}: CareerPlanStatusDialogProps) {
  if (!mode) return null;

  const copy = STATUS_DIALOG_COPY[mode];

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline={copy.headline}
      disableClose={saving}
      className="sm:max-w-[423px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={onClose} testId={copy.cancelTestId} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={onConfirm}
            disabled={saving}
            testId={copy.confirmTestId}
            icon={saving ? "progress_activity" : undefined}
            iconClassName={saving ? "animate-spin w-6 h-6 !block" : undefined}
          >
            {saving ? copy.confirming : copy.confirm}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-body-medium">{copy.body}</p>
        {error ? (
          <p className="text-body-medium text-sys-brand-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
